import { ReactNode, useRef } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';

import { RefreshToken } from '@crossnokaye/typescript-sdk/models/components';
import { Form, FormSaveBar, useToaster } from '@crossnokaye/ux-common';

import { useSetRefreshTokenAttributes } from '@/features/tokens/hooks/useSetRefreshTokenAttributes';

const editTokenSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
});

type EditTokenFormValues = z.infer<typeof editTokenSchema>;

interface EditTokenFormProps {
  token: RefreshToken;
  children: (form: UseFormReturn<EditTokenFormValues>) => ReactNode;
  onSuccess?: () => void;
}

export const EditTokenForm = ({ token, children, onSuccess }: EditTokenFormProps) => {
  const { addSuccessToast, addErrorToast } = useToaster();
  const queryClient = useQueryClient();
  const formRef = useRef<HTMLFormElement>(null);

  const form = useForm<EditTokenFormValues>({
    resolver: zodResolver(editTokenSchema),
    defaultValues: {
      name: token.name ?? '',
      description: token.description ?? '',
    },
  });

  const { mutate: updateToken, isPending: isSubmitting } = useSetRefreshTokenAttributes({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['iam', 'ListUserRefreshTokens'] });
      addSuccessToast('Token updated successfully');
      onSuccess?.();
      form.reset({
        name: form.getValues().name,
        description: form.getValues().description,
      });
    },
    onError: () => {
      addErrorToast('Failed to update token');
    },
  });

  const onSubmit = (values: EditTokenFormValues) => {
    updateToken({
      userId: token.principal.principalId,
      tokenId: token.id,
      setRefreshTokenAttributesRequestBody: {
        name: values.name,
        description: values.description,
      },
    });
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} ref={formRef}>
        {children(form)}
        <FormSaveBar formRef={formRef} isSubmitting={isSubmitting} />
      </form>
    </Form>
  );
};
